// "Filtered Statistics" box — count + combined LTV per AM × Stage over the
// CURRENT filtered result set. Appears beside the LTV totals when a stage
// filter or date range is active. Leads variant also shows conversion rate.
import { BarChart3, TrendingUp } from "lucide-react";

const GBP = (n) =>
  n == null ? "£0" : "£" + Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 });

export default function FilteredStatsBox({ stats, pipeline = "deals", periodLabel }) {
  if (!stats) return null;
  const rows = stats.by_am_stage || [];
  const totals = stats.totals || {};
  const conversion = stats.conversion || null;

  // Group rows by AM for a readable "Allen → Quote Sent: 14 · £X" layout.
  const byAm = {};
  for (const r of rows) {
    (byAm[r.am] = byAm[r.am] || []).push(r);
  }
  const ams = Object.keys(byAm).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));

  return (
    <div
      data-testid="filtered-stats-box"
      className="rounded-lg border border-emerald-500/25 bg-emerald-500/[0.04] p-3 min-w-[260px] max-w-[460px]"
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-300 flex items-center gap-1.5">
          <BarChart3 className="w-3 h-3" /> Filtered Statistics
          {periodLabel && <span className="text-emerald-500/60 normal-case tracking-normal">· {periodLabel}</span>}
        </div>
        <div className="text-[11px] text-slate-300 font-mono tabular-nums">
          <span className="text-white font-semibold">{totals.count ?? 0}</span>
          {pipeline === "deals" && totals.ltv != null && (
            <> · <span className="text-emerald-300 font-semibold">{GBP(totals.ltv)}</span></>
          )}
        </div>
      </div>

      {pipeline === "leads" && conversion && totals.count > 0 && (
        <div className="mb-2 flex items-center gap-1.5 text-[11.5px] text-sky-200" data-testid="filtered-stats-conversion-total">
          <TrendingUp className="w-3.5 h-3.5 text-sky-300" />
          Conversion: <span className="font-semibold text-white">{totals.rate ?? 0}%</span>
          <span className="text-slate-400">({totals.converted ?? 0} / {totals.count} converted)</span>
        </div>
      )}

      {ams.length === 0 ? (
        <div className="text-[11px] text-slate-500 font-mono py-1">No matching records.</div>
      ) : (
        <div className="space-y-2 max-h-[320px] overflow-y-auto pr-1">
          {ams.map((am) => {
            const conv = conversion?.find((c) => c.am === am);
            return (
              <div key={am} data-testid={`filtered-stats-am-${am}`} className="border-t border-white/[0.05] pt-1.5 first:border-t-0 first:pt-0">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[12px] font-semibold text-white truncate">{am}</span>
                  {pipeline === "leads" && conv && (
                    <span className="text-[10px] font-mono text-sky-300 shrink-0">{conv.rate}% conv</span>
                  )}
                </div>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {byAm[am].map((r) => (
                    <span
                      key={r.stage_key}
                      data-testid={`filtered-stats-chip-${am}-${r.stage_key}`}
                      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-white/[0.04] border border-white/10 text-[11px]"
                      title={`${am} – ${r.stage_label}: ${r.count} ${pipeline}${pipeline === "deals" ? ` · ${GBP(r.ltv)} LTV` : ""}${r.open_value ? ` · ${GBP(r.open_value)} open` : ""}`}
                    >
                      <span className="text-slate-300">{r.stage_label}</span>
                      <span className="text-white font-semibold tabular-nums">{r.count}</span>
                      {pipeline === "deals" && (
                        <span className="text-emerald-300 tabular-nums">{GBP(r.ltv)}</span>
                      )}
                      {pipeline === "deals" && r.stage_key === "quote_sent" && r.open_value > 0 && (
                        <span className="text-orange-300 tabular-nums" title="Open quote value">({GBP(r.open_value)} open)</span>
                      )}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
